import React, { useContext, useState, useEffect } from 'react';
import { UserContext } from '../contexts/UserContext';
import { checkTokenAndLogout } from '../utils/auth';
import styles from './EditProfilePage.module.css';
import { getPageContainerStyles, getSearchFormStyles, getModalStyles } from '../utils/styleUtils';
import { useLocation } from 'react-router-dom';
import { getThemeForPage } from '../styles/themes';

// SaveIcon 컴포넌트
const SaveIcon = ({ activeColor = '#00ff00', inactiveColor = '#f0f0f0', hoverColor = '#ffff00' }) => (
  <svg
    width="28"
    height="28"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={styles.saveIcon}
    style={{
      '--normal-color': inactiveColor,
      '--hover-color': hoverColor
    }}
  >
    <path
      d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z"
      stroke="var(--normal-color)"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <polyline
      points="17,21 17,13 7,13 7,21"
      stroke="var(--normal-color)"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <polyline
      points="7,3 7,8 15,8"
      stroke="var(--normal-color)"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// getLocale 함수 제거 - useLanguage 훅 사용

const TEXT = {
  title: { fr: 'MODIFIER MES INFORMATIONS', ko: '내 정보 수정', en: 'EDIT MY INFORMATION' },
  id: { fr: 'Identifiant', ko: '아이디', en: 'ID' },
  name: { fr: 'Nom', ko: '닉네임', en: 'Name' },
  password: { fr: 'Mot de passe', ko: '비밀번호', en: 'Password' },
  passwordPh: { fr: 'Saisir uniquement pour changer', ko: '비밀번호 변경시에만 입력', en: 'Enter only to change password' },
  email: { fr: 'E-mail', ko: '이메일', en: 'Email' },
  status: { fr: 'Statut', ko: '상태', en: 'Status' },
  statusOpt: {
    fr: ['Sélectionner', 'Ouvert', 'En vacances', 'Guest à l\'étranger', 'Autre'],
    ko: ['선택', '영업중', '휴가중', '해외게스트출장', '기타'],
    en: ['Select', 'Open', 'On vacation', 'Guest Abroad', 'Other']
  },
  location: { fr: 'Lieu', ko: '위치', en: 'Location' },
  color: { fr: 'Couleur', ko: '컬러', en: 'Color' },
  colorNotice: { fr: '(Contactez l\'admin pour modifier)', ko: '(수정은 관리자에게 문의)', en: '(Contact admin to change)' },
  level: { fr: 'Niveau', ko: '권한', en: 'Level' },
  joined: { fr: 'Date d\'inscription', ko: '가입일', en: 'Joined' },
  save: { fr: 'Enregistrer', ko: '저장', en: 'Save' },
  cancel: { fr: 'Annuler', ko: '취소', en: 'Cancel' },
  loginRequired: { fr: 'Connexion requise.', ko: '로그인이 필요합니다.', en: 'Login required.' },
  updated: { fr: 'Profil mis à jour.', ko: '정보가 수정되었습니다.', en: 'Profile updated.' },
  fail: { fr: 'Échec de la modification', ko: '수정 실패', en: 'Update failed' },
  error: { fr: 'Erreur lors de la modification', ko: '수정 중 오류', en: 'Error during update' },
  updateSuccess: { fr: 'Modification terminée !', ko: '수정 완료!', en: 'Update completed!' },
  updateFailed: { fr: 'Échec de la modification !', ko: '수정 실패!', en: 'Update failed!' },
  deposit: { fr: 'Montant acompte par défaut', ko: '선금 기본금액', en: 'Default Deposit Amount' },
  paypal: { fr: 'Adresse PayPal', ko: '페이팔 주소', en: 'PayPal Address' },
  bank: { fr: 'Coordonnées bancaires', ko: '은행 계좌정보', en: 'Bank Account Info' },
  lydia: { fr: 'Adresse Lydia', ko: '리디아 주소', en: 'Lydia Address' },
  language: { fr: 'Langue', ko: '언어', en: 'Language' },
  langOptions: {
    ko: { label: '한국어', value: 'ko' },
    en: { label: 'English', value: 'en' },
    fr: { label: 'Français', value: 'fr' }
  }
};

export default function EditProfilePage() {
  const { userInfo, setUserInfo } = useContext(UserContext);
  const lang = userInfo?.preferred_language || 'ko';
  const location = useLocation();

  // 공통 스타일 가져오기
  const pageContainerStyles = getPageContainerStyles();
  // eslint-disable-next-line no-unused-vars
  const searchStyles = getSearchFormStyles();
  // eslint-disable-next-line no-unused-vars
  const modalStyles = getModalStyles();

  // 테마 설정
  const currentTheme = getThemeForPage(location.pathname);

  // 사용자가 선택한 테마 확인
  const userSelectedTheme = localStorage.getItem('user_theme') || 'modern';
  const isDosTheme = userSelectedTheme === 'dos';

  const [form, setForm] = useState({
    user_name: userInfo?.user_name || '',
    password: '',
    email: userInfo?.email || '',
    status: userInfo?.status || '',
    location: userInfo?.location || '',
    color: userInfo?.color || '#cccccc',
    deposit_default_amount: userInfo?.deposit_default_amount || '',
    paypal_address: userInfo?.paypal_address || '',
    bank_account_info: userInfo?.bank_account_info || '',
    lydia_address: userInfo?.lydia_address || '',
    preferred_language: userInfo?.preferred_language || 'ko',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // 컴포넌트 마운트 시 토큰 검사
  useEffect(() => {
    if (checkTokenAndLogout()) {
      return;
    }
  }, []);

  // DOS 테마에 맞게 PWA 노치 여백 배경색 설정
  useEffect(() => {
    // theme-color 메타 태그 업데이트
    const themeColorMeta = document.querySelector('meta[name="theme-color"]');
    if (themeColorMeta) {
      themeColorMeta.setAttribute('content', currentTheme.backgroundColor);
    }

    // body 배경색도 테마로 변경 (PWA 노치 여백 포함)
    const originalBodyBg = document.body.style.backgroundColor;
    document.body.style.backgroundColor = currentTheme.backgroundColor;

    // 정리 함수: 페이지를 벗어날 때 기본값으로 복원
    return () => {
      if (themeColorMeta) {
        themeColorMeta.setAttribute('content', '#fffdf8'); // 기본 검은색으로 복원
      }
      document.body.style.backgroundColor = originalBodyBg; // 원래 배경색으로 복원
    };
  }, [currentTheme.backgroundColor]);

  // 토큰 만료 체크 (컴포넌트 렌더링 전)
  if (checkTokenAndLogout()) {
    return <div style={{ padding: '3px', textAlign: 'center' }}>Redirecting...</div>;
  }

  if (!userInfo) return <div>{TEXT.loginRequired[lang]}</div>;

  const handleChange = e => {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value }));
  };

  const handleSubmit = async e => {
    e.preventDefault();

    // 토큰 만료 체크
    if (checkTokenAndLogout()) {
      return;
    }

    setLoading(true);
    setError('');
    try {
      const payload = { ...form };

      // 비밀번호 필드 처리 개선
      if (payload.password === '') {
        // 빈 문자열인 경우 undefined로 설정 (서버에서 무시하도록)
        payload.password = undefined;
      }



      if (userInfo.level === 'client') {
        payload.status = userInfo.status;
        payload.location = userInfo.location;
        payload.color = userInfo.color;
        // 선금, 페이팔, 은행, 리디아는 클라이언트도 수정 가능하게 둠
      }

      const res = await fetch('/api/me', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
      });



      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        console.error('서버 에러 응답:', errorData);
        throw new Error(errorData.message || TEXT.fail[lang]);
      }

      const updated = await res.json();


      setUserInfo(updated.user);
      alert(TEXT.updateSuccess[lang]);
      window.location.reload();
    } catch (e) {
      console.error('프로필 업데이트 에러:', e);
      setError(e.message || TEXT.error[lang]);
      alert(e.message || TEXT.updateFailed[lang]);
      window.location.reload();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        overflow: 'auto',
        height: '100%',
        width: '100%',
        padding: '0',
        backgroundColor: currentTheme.backgroundColor,
        border: isDosTheme ? 'none !important' : '1px solid #e0e0e0 !important',
        scrollbarWidth: 'none', // Firefox
        msOverflowStyle: 'none', // IE/Edge
        '--active-color': currentTheme.activeColor,
        '--inactive-color': currentTheme.inactiveColor,
        '--background-color': currentTheme.backgroundColor,
        '--border-color': currentTheme.borderColor
      }}
    >
      {/* 페이지 타이틀 */}
      <h1 className={pageContainerStyles.pageTitle}>
        {TEXT.title[lang]}
      </h1>

      {/* 프로필 수정 폼 */}
      <div
        className={styles.formContainer}
        style={{
          background: isDosTheme ? '#000080' : 'transparent', // 도스 테마: 파란색, 기본 테마: 투명
          border: isDosTheme ? 'none' : '1px solid #e0e0e0',
          padding: '10px 10px 5px 10px',
          borderRadius: '8px',
          marginBottom: '15px',
          width: '85vw',
          maxWidth: '400px',
          margin: '0 auto',
          color: isDosTheme ? '#ffffff' : '#333333',
          '--active-color': isDosTheme ? '#00ff00' : '#c00d0d',
          '--inactive-color': isDosTheme ? '#ffffff' : '#333333',
          '--border-color': isDosTheme ? 'transparent' : '#e0e0e0'
        }}>
        <form onSubmit={handleSubmit}>
          <div style={{ display: 'flex', gap: '10px' }}>
            <div className={styles.formGroup} style={{ flex: 1 }}>
              <label className={styles.label}>{TEXT.id[lang]}</label>
              <input
                value={userInfo.user_login_id}
                disabled
                className={styles.input}
                style={{ border: `1px dashed ${isDosTheme ? '#00ff00' : '#ccc'}` }}
              />
            </div>
            <div className={styles.formGroup} style={{ flex: 1 }}>
              <label className={styles.label}>{TEXT.name[lang]}</label>
              <input
                name="user_name"
                value={form.user_name}
                onChange={handleChange}
                required
                className={styles.input}
                style={{ border: `1px dashed ${isDosTheme ? '#00ff00' : '#ccc'}` }}
              />
            </div>
          </div>
          <div className={styles.formGroup}>
            <label className={styles.label}>{TEXT.password[lang]}</label>
            <input
              name="password"
              type="password"
              value={form.password}
              onChange={handleChange}
              autoComplete="new-password"
              placeholder={TEXT.passwordPh[lang]}
              className={styles.input}
              style={{ border: `1px dashed ${isDosTheme ? '#00ff00' : '#ccc'}` }}
            />
          </div>
          <div className={styles.formGroup}>
            <label className={styles.label}>{TEXT.email[lang]}</label>
            <input
              name="email"
              value={form.email}
              onChange={handleChange}
              className={styles.input}
              style={{ border: `1px dashed ${isDosTheme ? '#00ff00' : '#ccc'}` }}
            />
          </div>
          <div className={styles.formGroup}>
            <label className={styles.label}>{TEXT.language[lang]}</label>
            <select
              name="preferred_language"
              value={form.preferred_language}
              onChange={handleChange}
              className={styles.input}
              style={{ border: `1px dashed ${isDosTheme ? '#00ff00' : '#ccc'}` }}
            >
              <option value="ko">{TEXT.langOptions.ko.label}</option>
              <option value="en">{TEXT.langOptions.en.label}</option>
              <option value="fr">{TEXT.langOptions.fr.label}</option>
            </select>
          </div>
          {userInfo.level !== 'client' && userInfo.level !== 'guest_artist' && (
            <>
              <div className={styles.formGroup}>
                <label className={styles.label}>{TEXT.status[lang]}</label>
                <select
                  name="status"
                  value={form.status}
                  onChange={handleChange}
                  className={styles.input}
                  style={{ border: `1px dashed ${isDosTheme ? '#00ff00' : '#ccc'}` }}
                >
                  {TEXT.statusOpt[lang].map((opt, idx) => (
                    <option key={opt} value={opt === TEXT.statusOpt[lang][0] ? '' : opt}>{opt}</option>
                  ))}
                </select>
              </div>
              <div className={styles.formGroup}>
                <label className={styles.label}>{TEXT.location[lang]}</label>
                <input
                  name="location"
                  value={form.location}
                  onChange={handleChange}
                  className={styles.input}
                  style={{ border: `1px dashed ${isDosTheme ? '#00ff00' : '#ccc'}` }}
                />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.label}>{TEXT.color[lang]}</label>
                <input
                  name="color"
                  type="color"
                  value={form.color}
                  disabled
                  className={styles.input}
                  style={{ border: `1px dashed ${isDosTheme ? '#00ff00' : '#ccc'}` }}
                />
                <span className={styles.colorNotice}>{TEXT.colorNotice[lang]}</span>
              </div>
            </>
          )}
          <div className={styles.formGroup} style={{ display: 'flex', gap: '20px' }}>
            <div style={{ flex: 1 }}>
              <label className={styles.label}>{TEXT.level[lang]}</label>
              <div className={styles.infoText}>{userInfo.level}</div>
            </div>
            <div style={{ flex: 1 }}>
              <label className={styles.label}>{TEXT.joined[lang]}</label>
              <div className={styles.infoText}>{userInfo.joined_at ? userInfo.joined_at.split('T')[0] : ''}</div>
            </div>
          </div>
          {error && <div className={styles.errorMsg}>{error}</div>}
          <button type="submit" disabled={loading} className={styles.submitBtn} title={TEXT.save[lang]}>
            <SaveIcon inactiveColor={currentTheme.activeColor} hoverColor="#ffff00" />
          </button>
        </form>
      </div>

      {/* 하단 여백 */}
      <div style={{ height: '60px' }}></div>
    </div>
  );
} 